// ============================================================================
// useStandardConfirms — promise-based wrappers around the enterprise template's
// ConfirmDialog component. Screens no longer wire deleteOpen / cancelOpen /
// resetOpen state by hand — call `await confirmDelete(...)` and await the
// boolean.
// ============================================================================
// Returns BOTH:
//   - The async confirm-* functions (await one, get true/false)
//   - A `<ConfirmDialogPortal />` JSX element to render once at the bottom of
//     the screen. The portal renders the currently-active dialog if any.
//
// USAGE (in a screen):
//
//   const { confirmDelete, confirmDiscard, confirmReset, ConfirmDialogPortal }
//     = useStandardConfirms();
//
//   const handleDelete = async (record) => {
//     const ok = await confirmDelete({
//       resourceLabel: record.name,
//       danger: true,
//     });
//     if (ok) await service.delete(record.id);
//   };
//
//   const handleCancel = async () => {
//     if (!isDirty) { close(); return; }
//     const ok = await confirmDiscard();
//     if (ok) close();
//   };
//
//   return (
//     <div className="module-master-container">
//       {/* ... screen content ... */}
//       <ConfirmDialogPortal />
//     </div>
//   );

import React, { useCallback, useRef, useState } from 'react';
import { ConfirmDialog } from '../modals/common/ConfirmDialog';

interface ConfirmDeleteOptions {
  /** Display label of the resource being deleted (e.g. "Sales Order SO-1024"). Default "this record". */
  resourceLabel?: string;
  /** Override the default title. */
  title?: string;
  /** Override the default message. */
  message?: string;
  /** Override the confirm label. Default "Delete". */
  confirmLabel?: string;
}

interface ConfirmDiscardOptions {
  /** Override the default title. Default "Discard unsaved changes?". */
  title?: string;
  /** Override the default message. */
  message?: string;
  /** Override the confirm label. Default "Discard". */
  confirmLabel?: string;
}

interface ConfirmResetOptions {
  /** Override the default title. Default "Reset to defaults?". */
  title?: string;
  /** Override the default message. */
  message?: string;
  /** Override the confirm label. Default "Reset". */
  confirmLabel?: string;
}

interface ConfirmExitOptions {
  /** Override the default title. Default "Exit screen?". */
  title?: string;
  /** Override the default message. */
  message?: string;
  /** Override the confirm label. Default "Exit". */
  confirmLabel?: string;
}

interface ConfirmCustomOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger' | 'success';
}

interface ActiveConfirm {
  title:           string;
  message:         string;
  confirmLabel:    string;
  cancelLabel:     string;
  variant:         'default' | 'danger' | 'success';
  resolve:         (ok: boolean) => void;
}

interface UseStandardConfirmsReturn {
  /**
   * Show a delete confirmation. Default: "danger" variant, "Delete X?" title,
   * "X will be permanently deleted. This cannot be undone." message.
   * Resolves to `true` if the user clicks Delete; `false` otherwise.
   */
  confirmDelete: (opts?: ConfirmDeleteOptions) => Promise<boolean>;

  /**
   * Show an "unsaved changes" confirmation. Default: "default" variant,
   * "Discard unsaved changes?" title. Resolves to `true` if the user
   * confirms discarding.
   */
  confirmDiscard: (opts?: ConfirmDiscardOptions) => Promise<boolean>;

  /**
   * Show a reset confirmation (form / filters / preferences). Default:
   * "default" variant, "Reset to defaults?" title.
   */
  confirmReset: (opts?: ConfirmResetOptions) => Promise<boolean>;

  /**
   * Show an exit-screen confirmation (typically called from logout / tab
   * close handler when work is dirty). Default: "default" variant.
   */
  confirmExit: (opts?: ConfirmExitOptions) => Promise<boolean>;

  /**
   * Show a fully-custom confirmation. Use only when none of the standard
   * confirms fits (most screens should NOT need this).
   */
  confirmCustom: (opts: ConfirmCustomOptions) => Promise<boolean>;

  /** Render this once at the bottom of the screen. */
  ConfirmDialogPortal: React.FC;
}

export function useStandardConfirms(): UseStandardConfirmsReturn {
  const [active, setActive] = useState<ActiveConfirm | null>(null);
  // Avoid stacking confirms; if a second is requested while one is open,
  // the first one resolves with false and the second one takes over.
  const activeRef = useRef<ActiveConfirm | null>(null);
  activeRef.current = active;

  const show = useCallback((c: Omit<ActiveConfirm, 'resolve'>) => {
    return new Promise<boolean>((resolve) => {
      if (activeRef.current) activeRef.current.resolve(false);
      setActive({ ...c, resolve });
    });
  }, []);

  const confirmDelete = useCallback((opts: ConfirmDeleteOptions = {}) => {
    const label = opts.resourceLabel ?? 'this record';
    return show({
      title:        opts.title        ?? 'Delete?',
      message:      opts.message      ?? `"${label}" will be permanently deleted. This cannot be undone.`,
      confirmLabel: opts.confirmLabel ?? 'Delete',
      cancelLabel:  'Cancel',
      variant:      'danger',
    });
  }, [show]);

  const confirmDiscard = useCallback((opts: ConfirmDiscardOptions = {}) => {
    return show({
      title:        opts.title        ?? 'Discard unsaved changes?',
      message:      opts.message      ?? 'Your changes have not been saved. Closing now will discard them.',
      confirmLabel: opts.confirmLabel ?? 'Discard',
      cancelLabel:  'Keep editing',
      variant:      'default',
    });
  }, [show]);

  const confirmReset = useCallback((opts: ConfirmResetOptions = {}) => {
    return show({
      title:        opts.title        ?? 'Reset to defaults?',
      message:      opts.message      ?? 'Current values will be cleared. This cannot be undone.',
      confirmLabel: opts.confirmLabel ?? 'Reset',
      cancelLabel:  'Cancel',
      variant:      'default',
    });
  }, [show]);

  const confirmExit = useCallback((opts: ConfirmExitOptions = {}) => {
    return show({
      title:        opts.title        ?? 'Exit screen?',
      message:      opts.message      ?? 'You have unsaved changes. Are you sure you want to exit?',
      confirmLabel: opts.confirmLabel ?? 'Exit',
      cancelLabel:  'Stay',
      variant:      'default',
    });
  }, [show]);

  const confirmCustom = useCallback((opts: ConfirmCustomOptions) => {
    return show({
      title:        opts.title,
      message:      opts.message,
      confirmLabel: opts.confirmLabel ?? 'Confirm',
      cancelLabel:  opts.cancelLabel  ?? 'Cancel',
      variant:      opts.variant      ?? 'default',
    });
  }, [show]);

  const handleConfirm = useCallback(() => {
    if (!active) return;
    active.resolve(true);
    setActive(null);
  }, [active]);

  const handleCancel = useCallback(() => {
    if (!active) return;
    active.resolve(false);
    setActive(null);
  }, [active]);

  const ConfirmDialogPortal = useCallback(() => {
    if (!active) return null;
    return (
      <ConfirmDialog
        open={true}
        title={active.title}
        message={active.message}
        confirmLabel={active.confirmLabel}
        cancelLabel={active.cancelLabel}
        variant={active.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    );
  }, [active, handleConfirm, handleCancel]);

  return {
    confirmDelete,
    confirmDiscard,
    confirmReset,
    confirmExit,
    confirmCustom,
    ConfirmDialogPortal,
  };
}

export default useStandardConfirms;
